/* eslint-disable react/prop-types */
// Panneau de notifications de la cloche — portage du design Claude
// (Directeur/Notifications.jsx · NotifPanel).
//
// Structure du design conservée : en-tête « Notifications » + compteur de non
// lues + « Tout lire », trois filtres en pastilles (Toutes / Non lues /
// Urgentes), lignes groupées par jour avec catégorie, horodatage, point orange
// de non-lu, titre, sous-titre et bouton d'action, pied de panneau avec accès à
// l'historique et aux préférences.
//
// Données réelles : GET /api/directeur/dashboard/ — la file `actions_requises`
// (enseignants absents, incidents à traiter, validations en attente, absences
// non justifiées), les `insights` calculés côté serveur et les prochains
// éléments d'`agenda`. Le design listait huit notifications écrites en dur.
//
// L'état « lu » n'existe pas côté serveur pour ces éléments dérivés : il est
// mémorisé localement par identifiant stable (localStorage), ce qui suffit à
// faire disparaître la pastille sans jamais masquer une action réellement en
// attente — l'élément reste dans la liste, seul son état visuel change.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  UserCheck,
  Flag,
  CheckCircle2,
  Clock,
  MessageSquare,
  TrendingDown,
  CalendarCheck,
  Sparkles,
  ArrowRight,
  CheckCheck,
  Settings,
} from "lucide-react";
import { getDirecteurDashboard } from "../../api/directeurApi";

const STORAGE_KEY = "ss_directeur_notifs_lues";

const KIND = {
  urgent: { label: "Urgent", text: "text-red-600", bg: "bg-red-50", border: "border-red-600" },
  warn: { label: "Attention", text: "text-amber-600", bg: "bg-orange-50", border: "border-amber-600" },
  info: { label: "Info", text: "text-blue-700", bg: "bg-blue-50", border: "border-blue-700" },
};

// Icônes renvoyées par le backend (chaînes) -> composants lucide.
const ICONS = {
  "user-check": UserCheck,
  flag: Flag,
  "check-circle": CheckCircle2,
  clock: Clock,
  "message-square": MessageSquare,
  "trending-down": TrendingDown,
  "calendar-check": CalendarCheck,
};

const LEVEL_TO_KIND = { critical: "urgent", warning: "warn", attention: "info" };
const TONE_TO_KIND = { red: "warn", amber: "warn", orange: "warn", blue: "info", emerald: "info" };

// Catégorie affichée en tête de ligne, déduite de l'identifiant d'action.
const ACTION_CAT = {
  "profs-absents": "Personnel",
  incidents: "Incident",
  validations: "RH",
  absences: "Vie scolaire",
};

function lireStockage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function ecrireStockage(ids) {
  try {
    // On borne pour ne pas laisser grossir indéfiniment le stockage local.
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids.slice(-200)));
  } catch {
    /* stockage indisponible (navigation privée) — état simplement non mémorisé */
  }
}

function heureDe(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

function jourDe(iso) {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "À venir";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  if (diff <= 0) return "Aujourd'hui";
  if (diff === 1) return "Demain";
  return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "short" });
}

/**
 * Construit la liste de notifications à partir de la charge utile du dashboard.
 * Chaque élément reçoit un identifiant stable, seule base fiable pour mémoriser
 * l'état « lu » d'un signal recalculé à chaque appel.
 */
export function construireNotifications(data) {
  const heure = heureDe(data?.generated_at);
  const items = [];

  (data?.actions_requises || []).forEach((a) => {
    items.push({
      id: `action:${a.id}`,
      day: "Aujourd'hui",
      time: heure,
      kind: LEVEL_TO_KIND[a.level] || "info",
      Icon: ICONS[a.icon] || Flag,
      cat: ACTION_CAT[a.id] || "Direction",
      title: a.title,
      sub: a.detail,
      cta: a.action,
      route: a.route,
    });
  });

  (data?.insights || []).forEach((s, i) => {
    items.push({
      id: `insight:${s.cat}:${i}`,
      day: "Aujourd'hui",
      time: heure,
      kind: TONE_TO_KIND[s.tone] || "info",
      Icon: ICONS[s.icon] || Sparkles,
      cat: s.cat || "Signal",
      title: s.text,
      sub: "Signal détecté sur les 30 derniers jours",
      cta: s.cta,
      route: s.route,
    });
  });

  (data?.agenda || []).slice(0, 5).forEach((a) => {
    items.push({
      id: `agenda:${a.date}:${a.label}`,
      day: jourDe(a.date),
      time: a.time,
      kind: a.hot ? "warn" : "info",
      Icon: CalendarCheck,
      cat: "Agenda",
      title: a.label,
      sub: a.type === "meeting" ? "Rendez-vous confirmé" : "Absence de personnel planifiée",
      cta: "Ouvrir",
      route: "/directeur/events",
    });
  });

  return items;
}

/**
 * Charge les notifications et gère l'état « lu ».
 * Monté par le shell : un seul appel au montage, pas à chaque navigation.
 */
export function useDirecteurNotifications() {
  const [items, setItems] = useState([]);
  const [lues, setLues] = useState(lireStockage);

  useEffect(() => {
    let alive = true;
    getDirecteurDashboard()
      .then((d) => alive && setItems(construireNotifications(d)))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const marquerLu = useCallback((id) => {
    setLues((prev) => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      ecrireStockage(next);
      return next;
    });
  }, []);

  const toutMarquerLu = useCallback(() => {
    setLues(() => {
      const next = [...new Set(items.map((i) => i.id))];
      ecrireStockage(next);
      return next;
    });
  }, [items]);

  const nonLues = useMemo(() => items.filter((i) => !lues.includes(i.id)).length, [items, lues]);

  return { items, lues, nonLues, marquerLu, toutMarquerLu };
}

function NotifRow({ n, unread, onGo }) {
  const k = KIND[n.kind] || KIND.info;
  return (
    <button
      type="button"
      onClick={() => onGo(n)}
      className={`group flex w-full gap-2.5 rounded-[10px] p-2.5 text-left transition-colors hover:bg-[#F8F9FB] ${
        unread ? "bg-[#FFFCF8]" : ""
      }`}
    >
      <span className={`mt-px flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${k.bg}`}>
        <n.Icon size={14} className={k.text} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="mb-0.5 flex items-baseline gap-1.5">
          <span className={`text-[9.5px] font-bold uppercase tracking-wider ${k.text}`}>{n.cat}</span>
          <span className="ml-auto shrink-0 text-[10px] text-gray-400">{n.time}</span>
          {unread && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />}
        </span>
        <span
          className={`block text-[12.5px] leading-snug text-gray-900 ${unread ? "font-bold" : "font-semibold"}`}
        >
          {n.title}
        </span>
        <span className="mt-0.5 block truncate text-[11px] leading-relaxed text-gray-500">{n.sub}</span>
        {n.cta && (
          <span
            className={`mt-1.5 inline-flex h-[25px] items-center rounded-[7px] border px-2.5 text-[11px] font-bold transition-colors ${
              unread ? `${k.border} ${k.bg} ${k.text}` : "border-gray-200 bg-white text-gray-600"
            } group-hover:${k.text}`}
          >
            {n.cta}
          </span>
        )}
      </span>
    </button>
  );
}

export default function NotifPanel({ items, lues, nonLues, onRead, onReadAll, onClose }) {
  const navigate = useNavigate();
  const [filtre, setFiltre] = useState("all");

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const urgentes = items.filter((n) => n.kind === "urgent").length;
  const affichees = items.filter((n) => {
    if (filtre === "unread") return !lues.includes(n.id);
    if (filtre === "urgent") return n.kind === "urgent";
    return true;
  });
  const jours = [...new Set(affichees.map((n) => n.day))];

  const go = (n) => {
    onRead(n.id);
    if (n.route) navigate(n.route);
    onClose();
  };

  const tabs = [
    { id: "all", l: "Toutes", n: items.length },
    { id: "unread", l: "Non lues", n: nonLues },
    { id: "urgent", l: "Urgentes", n: urgentes },
  ];

  return (
    <div
      className="absolute -right-2 top-10 z-[300] flex w-[384px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-[14px] border border-gray-200 bg-white shadow-2xl"
      style={{ maxHeight: "min(560px, 80vh)" }}
      role="dialog"
      aria-label="Notifications"
    >
      <div className="flex items-center gap-2 px-3.5 pb-2.5 pt-3">
        <span className="text-sm font-bold text-gray-900">Notifications</span>
        {nonLues > 0 && (
          <span className="rounded-full bg-orange-50 px-1.5 py-0.5 text-[10px] font-bold text-orange-800">
            {nonLues} non lues
          </span>
        )}
        <button
          type="button"
          onClick={onReadAll}
          className="ml-auto flex h-[26px] items-center gap-1.5 rounded-[7px] px-2 text-[11px] font-semibold text-gray-500 transition-colors hover:bg-[#F8F9FB]"
        >
          <CheckCheck size={13} className="text-gray-400" />
          Tout lire
        </button>
      </div>

      <div className="flex gap-1.5 border-b border-gray-100 px-3.5 pb-2.5">
        {tabs.map((t) => {
          const on = filtre === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setFiltre(t.id)}
              className={`flex h-[27px] items-center gap-1.5 rounded-full border px-2.5 text-[11.5px] font-semibold transition-colors ${
                on ? "border-accent bg-orange-50 text-orange-800" : "border-gray-200 bg-white text-gray-500"
              }`}
            >
              {t.l}
              <span className={`font-bold ${on ? "text-accent" : "text-gray-400"}`}>{t.n}</span>
            </button>
          );
        })}
      </div>

      <div className="flex-1 overflow-y-auto p-1.5 pb-2">
        {affichees.length === 0 && (
          <div className="px-5 py-10 text-center">
            <CheckCircle2 size={22} className="mx-auto text-gray-300" />
            <div className="mt-2 text-[12.5px] text-gray-400">Rien à signaler ici.</div>
          </div>
        )}
        {jours.map((j) => (
          <div key={j}>
            <div className="px-3 pb-1 pt-2 text-[9.5px] font-bold uppercase tracking-wider text-gray-400">{j}</div>
            {affichees
              .filter((n) => n.day === j)
              .map((n) => (
                <NotifRow key={n.id} n={n} unread={!lues.includes(n.id)} onGo={go} />
              ))}
          </div>
        ))}
      </div>

      <div className="flex items-center border-t border-gray-100 bg-gray-50/60 px-3 py-2">
        <button
          type="button"
          onClick={() => {
            navigate("/directeur/compliance");
            onClose();
          }}
          className="flex h-7 items-center gap-1.5 rounded-[7px] px-2 text-[11.5px] font-bold text-accent transition-colors hover:bg-orange-50"
        >
          Voir tout l&apos;historique
          <ArrowRight size={12} />
        </button>
        <button
          type="button"
          onClick={() => {
            navigate("/directeur/settings");
            onClose();
          }}
          className="ml-auto flex h-7 items-center gap-1.5 rounded-[7px] px-2 text-[11.5px] font-semibold text-gray-400 transition-colors hover:bg-gray-100"
        >
          <Settings size={13} />
          Préférences
        </button>
      </div>
    </div>
  );
}
